import { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  type FreshStrawFreshEmbryoImportResponse,
  useImportFreshStrawFreshEmbryosMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FreshStrawFreshEmbryoImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (result: FreshStrawFreshEmbryoImportResponse) => void;
}

export function FreshStrawFreshEmbryoImportDialog({
  open,
  onOpenChange,
  onImported,
}: FreshStrawFreshEmbryoImportDialogProps) {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] =
    useState<FreshStrawFreshEmbryoImportResponse | null>(null);

  const [importFreshEmbryos, { isLoading }] =
    useImportFreshStrawFreshEmbryosMutation();

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!file) return;
    try {
      const response = await importFreshEmbryos({ file }).unwrap();

      setResult(response);
      onImported?.(response);

      const errorCount = response.errors?.length ?? 0;
      dispatch(
        addToast({
          severity: errorCount > 0 ? 'warn' : 'success',
          summary: 'Fresh-embryo import complete',
          detail: `Parsed ${response.parsed}, saved ${response.saved}${
            errorCount > 0
              ? `, ${errorCount} error${errorCount === 1 ? '' : 's'}`
              : ''
          }.`,
          life: ToastLife.LONG,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Import failed',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import fresh embryos</DialogTitle>
          <DialogDescription>
            Upload the completed fresh-embryo template. Each row is matched on{' '}
            <code>embryoid</code> to a staged record; straw #, stage, grade,
            recip, froze, and discard are applied and embryos are promoted into
            the main table.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fresh-straw-fresh-embryo-file">
              Workbook (.xlsx)
            </Label>
            <Input
              ref={fileInputRef}
              id="fresh-straw-fresh-embryo-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={isLoading}
            />
          </div>

          {result && (
            <div
              className="border-border bg-muted/30 rounded-md border p-3 text-sm"
              data-testid="fresh-straw-fresh-embryo-result"
            >
              <div className="font-semibold">
                Parsed {result.parsed} · Saved {result.saved}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-destructive mt-2 list-disc space-y-0.5 pl-5 text-xs">
                  {result.errors.slice(0, 10).map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li>…and {result.errors.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!file || isLoading}
          >
            {isLoading ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
